import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC, ReactNode } from "react";

interface KpiDialogScaffoldProps {
  open: boolean;
  onClose: () => void;
  title: string;
  explanation: string;
  isLoading: boolean;
  error: unknown;
  footerNote?: string;
  children: ReactNode;
}

export const KpiDialogScaffold: FC<KpiDialogScaffoldProps> = ({
  open,
  onClose,
  title,
  explanation,
  isLoading,
  error,
  footerNote,
  children,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      aria-labelledby="kpi-dialog-title"
      PaperProps={{
        sx: {
          width: "min(96vw, 1680px)",
          height: "92vh",
          maxWidth: "none",
          maxHeight: "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        id="kpi-dialog-title"
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          pb: 1.25,
          pt: 1.25,
          overflow: "hidden",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          <Tooltip title={title} placement="top" enterDelay={400}>
            <Box sx={{ width: "100%" }}>
              <Typography
                component="h2"
                variant="h6"
                sx={{
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={explanation} placement="top" enterDelay={400}>
            <Box sx={{ width: "100%", mt: 0.75 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                }}
              >
                {explanation}
              </Typography>
            </Box>
          </Tooltip>

          {!!footerNote && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1, opacity: 0.85 }}
            >
              {footerNote}
            </Typography>
          )}
        </Box>

        <IconButton
          aria-label="סגור"
          onClick={onClose}
          size="small"
          sx={{ flexShrink: 0, mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          pt: 2.5,
          pb: 2.5,
        }}
      >
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={36} />
          </Box>
        )}
        {(error ?? null) && (
          <Alert severity="error">
            לא ניתן לטעון את הפירוט. נסו שוב מאוחר יותר.
          </Alert>
        )}
        {!isLoading && !error && children}
      </DialogContent>
    </Dialog>
  );
};
